import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Select, Space, Spin, Tag, Typography, message } from 'antd';
import { SafetyCertificateOutlined, SaveOutlined } from '@ant-design/icons';
import AppCard from '../../../../components/cards/AppCard';
import OtpGateModal from '../../../../components/admin/OtpGateModal';
import { getAdminFormGroups, getModuleDefaultFields, saveModuleDefaultFields } from '../../../../services/adminApi';

const { Text } = Typography;

// DefaultFieldsPanel — Form Groups → Default Fields tab.
// Pick, per module, the form fields that must ALWAYS be shown and required.
// These fields' Show/Required toggles are then locked in the field editor and
// forced on at render (AddFormV1/EditFormV1), so no one can turn them off.
// Adding/updating the list is OTP-gated (code emailed to the approver).
export default function DefaultFieldsPanel({ moduleOptions = [] }) {
  const [module, setModule] = useState('');
  const [fieldOptions, setFieldOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    if (!module && moduleOptions.length) setModule(moduleOptions[0].value);
  }, [moduleOptions, module]);

  const load = useCallback(async (mod) => {
    if (!mod) return;
    setLoading(true);
    try {
      const [groups, defaults] = await Promise.all([
        getAdminFormGroups(mod).catch(() => []),
        getModuleDefaultFields(mod).catch(() => []),
      ]);
      // Flatten the module's form-group fields into a de-duplicated option list.
      const seen = new Set();
      const opts = [];
      (Array.isArray(groups) ? groups : []).forEach((g) => {
        (g.fields ?? []).forEach((f) => {
          const key = String(f.field ?? '').trim();
          if (!key || seen.has(key)) return;
          seen.add(key);
          opts.push({ value: key, label: `${f.label ?? key} · ${key}`, group: g.label ?? g.name });
        });
      });
      setFieldOptions(opts);
      setSaved(defaults);
      setSelected(defaults);
    } catch (e) {
      message.error(e?.message || 'Could not load default fields');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { queueMicrotask(() => { load(module); }); }, [module, load]);

  const dirty = useMemo(() => {
    if (selected.length !== saved.length) return true;
    const a = [...selected].sort();
    const b = [...saved].sort();
    return a.some((v, i) => v !== b[i]);
  }, [selected, saved]);

  async function doSave(otp) {
    const res = await saveModuleDefaultFields(module, selected, otp);
    setSaved(selected);
    return res;
  }

  return (
    <AppCard
      icon={<SafetyCertificateOutlined />}
      title="Default Fields"
      extra={(
        <Space>
          <Select
            size="small"
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
            placeholder="Module"
            value={module || undefined}
            options={moduleOptions}
            onChange={setModule}
          />
          <Button
            type="primary"
            size="small"
            icon={<SaveOutlined />}
            disabled={!module || !dirty}
            onClick={() => setGateOpen(true)}
          >
            Save (OTP)
          </Button>
        </Space>
      )}
    >
      <div className="admin-accent-bar" />
      <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
        Fields chosen here are <b>always shown and always required</b> on this module&apos;s add &amp; edit form.
        Their Show / Required switches are locked in Form Groups so no one can turn them off. Changing this
        list requires an OTP emailed to the approver.
      </Text>

      <Spin spinning={loading}>
        <Select
          mode="multiple"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: '100%' }}
          placeholder="Select the fields that must always show + be required"
          value={selected}
          options={fieldOptions}
          onChange={setSelected}
          maxTagCount="responsive"
        />
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Currently enforced ({saved.length}): </Text>
          {saved.length
            ? saved.map((f) => <Tag key={f} color="blue" style={{ marginTop: 4 }}>{f}</Tag>)
            : <Text type="secondary" style={{ fontSize: 12 }}>none</Text>}
        </div>
        {!loading && fieldOptions.length === 0 && (
          <Alert style={{ marginTop: 12 }} type="info" showIcon
            message="This module has no form-group fields yet — configure its Form Groups first." />
        )}
      </Spin>

      <OtpGateModal
        open={gateOpen}
        scope="defaultFields"
        action="save"
        title="Confirm Default Fields change"
        description="Adding or updating a module's default (always-shown + required) fields is a protected action."
        confirmLabel="Verify & Save"
        onCancel={() => setGateOpen(false)}
        onConfirm={doSave}
        onVerified={() => { setGateOpen(false); load(module); }}
      />
    </AppCard>
  );
}
